import { ArrowRightOutlined, CalendarOutlined, CheckOutlined, ClockCircleOutlined, DollarCircleOutlined, RobotOutlined, SafetyCertificateOutlined, SettingOutlined, TeamOutlined, UsergroupAddOutlined } from '@ant-design/icons';
import { Link } from "react-router-dom";

import Page from '../../Components/Page/Page';
import { TextService } from '../../Services/TextService';
import { Checks, CtaBanner, CtaButtons, FeatureGrid, IntegrationsStrip, MockEmail, MockMembers, MockPayments, SectionHead, Split, useScrollTop } from './Marketing2';
import './Start2.css';

// The start page ("/" and "/:lang/"). Styles are scoped under .start2 in Start2.css.
const Start2 = () => {
  const GetText = TextService.GetText;
  useScrollTop();

  const f = (n: string) => ({ title: GetText(`FeaturesMemberOrganisations_Feature${n}Title`), html: GetText(`FeaturesMemberOrganisations_Feature${n}Text`) });

  // The first six cards of "Fler funktioner" on /se/features/organisations; the full list is one click away.
  const features = [
    { icon: <SettingOutlined />, ...f("5") },
    { icon: <UsergroupAddOutlined />, ...f("8") },
    { icon: <TeamOutlined />, title: "Familjemedlemmar", html: "Koppla familjemedlemmar till en huvudmedlem och hantera hela familjen samlat." },
    { icon: <DollarCircleOutlined />, ...f("6") },
    { icon: <CalendarOutlined />, ...f("7") },
    { icon: <ClockCircleOutlined />, title: GetText("Start_EventFeatureTitle3"), html: GetText("Start_EventFeatureDescription3") },
  ];

  // Names are used as alt text (WCAG 1.1.1)
  const customerLogos = [
    { n: 1, name: "Mälarhöjdsföreningen" },
    { n: 3, name: "Kundförening" }, // TODO: real organisation name (lyre logo, no text in image)
    { n: 4, name: "ÖVF" },
    { n: 5, name: "Svenska Fjälledarorganisationen" },
    { n: 7, name: "Ingmarsö Brottö Museum" },
    { n: 8, name: "NK-Villans Vänner" },
  ];

  return <Page className='start2' pageTitle={GetText("PageTitle_Start")} metaDescription="OneFront360 – modernt medlemssystem för ideella föreningar och branschorganisationer. Prova gratis.">

    <div className="s2-top-band">
      {/* Hero */}
      <section className="s2-hero">
        <div className="s2-container s2-hero-grid">
          <div className="s2-hero-text">
            <span className="s2-eyebrow">Medlemssystem för föreningar &amp; företag</span>
            <h1>Mindre administration. <span className="s2-accent">Mer tid för medlemmarna.</span></h1>
            <p className="s2-lead">OneFront360 är samlingsplatsen för medlemsregister, event, utskick och betalningar – enkelt, automatiserat och säkert.</p>
            <CtaButtons />
            <ul className="s2-trust">
              <li><CheckOutlined /> {GetText("Start_EventInShort3")}</li>
              <li><CheckOutlined /> Svensk support</li>
              <li><CheckOutlined /> GDPR-säkert</li>
            </ul>
          </div>
          <div className="s2-hero-visual">
            <img alt="OneFront360" src={require('../../start2.jpg')} />
          </div>
        </div>
      </section>

      {/* Customer logos */}
      <section className="s2-logos">
        <div className="s2-container">
          <p className="s2-logos-title">Organisationer som redan använder OneFront</p>
          <div className="s2-logo-row">
            {customerLogos.map(logo => (
              <img key={logo.n} alt={logo.name} src={require(`../../Images/CustomerLogo/logo${logo.n}.png`)} />
            ))}
          </div>
        </div>
      </section>
    </div>

    {/* Three pillars */}
    <section className="s2-pillars-section">
      <div className="s2-container">
        <h2 className="s2-sr-only">Allt din organisation behöver</h2>
        <div className="s2-pillars">
          <Link className="s2-pillar s2-tint-blue" to="/se/medlemsregister">
            <span className="s2-pillar-icon"><TeamOutlined /></span>
            <h3>Flexibelt medlemsregister</h3>
            <p>Få kontroll och spårbarhet – du bestämmer innehåll, fält och åtkomst.</p>
            <span className="s2-more">Läs mer <ArrowRightOutlined /></span>
          </Link>
          <Link className="s2-pillar s2-tint-orange" to="/se/betalintegrationer">
            <span className="s2-pillar-icon"><RobotOutlined /></span>
            <h3>Smarta integrationer</h3>
            <p>Koppla ihop med hemsidan och ekonomisystemet. Automatisera medlemsinbetalningarna.</p>
            <span className="s2-more">Läs mer <ArrowRightOutlined /></span>
          </Link>
          <Link className="s2-pillar s2-tint-green" to="/se/gdpr-och-sakerhet">
            <span className="s2-pillar-icon"><SafetyCertificateOutlined /></span>
            <h3>Säkerhet &amp; GDPR</h3>
            <p>Säker och krypterad hantering av data, byggd för att följa GDPR.</p>
            <span className="s2-more">Läs mer <ArrowRightOutlined /></span>
          </Link>
        </div>
      </div>
    </section>

    <IntegrationsStrip />

    {/* Zig-zag blocks */}
    <section className="s2-section s2-bg-soft">
      <div className="s2-container">
        <Split eyebrow="Medlemshantering" title="Ett register som anpassar sig efter er" visual={<MockMembers />}>
          <p>Lägg till egna fält, hantera familjemedlemmar och ge medlemmarna en egen sida där de själva uppdaterar sina uppgifter.</p>
          <Checks items={["Import och export via Excel/CSV – vi hjälper er att flytta över registret","Webbformulär för nya medlemmar på er hemsida", "Egen färg och logotyp"]} />
        </Split>
        <Split reverse eyebrow="Kommunikation" title="Nå alla medlemmar med ett klick" visual={<MockEmail />}>
          <p>Skicka nyhetsbrev, kallelser och påminnelser via e-post och SMS. Spara porto och se exakt vad som har levererats.</p>
          <Checks items={["Egna mallar för betalning, info och påminnelser", "Loggar för all skickad e-post och SMS", "Event med anmälan och automatiska påminnelser"]} />
        </Split>
        <Split eyebrow="Betalningar" title="Full koll på medlemsavgifterna" visual={<MockPayments />}>
          <p>Följ upp inbetalningar manuellt eller automatiskt via integration mot bank, betaltjänster och ekonomisystem.</p>
          <Checks items={["Swish, kort, Bankgiro och Ping Payments", "Synk mot Fortnox och Spiris", "Påminn enkelt de som inte har betalat"]} />
        </Split>
      </div>
    </section>

    {/* Feature grid */}
    <section className="s2-section">
      <div className="s2-container">
        <SectionHead title={GetText("Start_FeaturesTitle")} />
        <FeatureGrid items={features} />
        <div className="s2-center">
          <Link className="s2-btn s2-btn-ghost" to="/se/features/organisations">{GetText("Start_SeeAllFeatures")} <ArrowRightOutlined /></Link>
        </div>
      </div>
    </section>

    <CtaBanner />
  </Page>
};

export default Start2;
